import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import {
  GeneralInfoDropdownOptions,
  GeneralInformationData,
  SourceFileData,
} from "../AddEditAutoImportModal/types/autoImportConfigContextTypes";
import { CONNECTION_TYPES_ENUM } from "../../constants/autoImportConnectionTypes";
import { SCHEDULED_RUN_TIMES_ENUM } from "../../constants/scheduledRunTimesTypes";

export interface AutoImportFormData
  extends GeneralInformationData,
    SourceFileData {}

export const defaultFormData: AutoImportFormData = {
  isActive: true,
  client: undefined,
  scheduleType: SCHEDULED_RUN_TIMES_ENUM.SELECT,
  scheduledTime: "00:00:00",
  forDtc: "",
  associatedInst: "",
  productType: undefined,
  clientProductGuid: "",
  jobName: "",
  sourceFileType: CONNECTION_TYPES_ENUM.SELECT,
  blobStorageUsername: "",
  blobStoragePassword: "",
  blobStorageFolder: "",
  ftpServer: "",
  sftpEnabled: false,
  ftpPort: 21,
  ftpUsername: "",
  ftpPassword: "",
  expectedFileName: "",
  archiveAfterProcessing: true,
  archivePath: "",
  fileHasHeaderRow: false,
};

interface AutoImportFormContextProps {
  formData: AutoImportFormData;
  updateFormData: (newData: Partial<AutoImportFormData>) => void;
  updateField: <K extends keyof AutoImportFormData>(
    field: K,
    value: AutoImportFormData[K]
  ) => void;
  resetFormData: () => void;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  generalInfoDropdownOptions: GeneralInfoDropdownOptions;
  updateGeneralInfoDropdownOptions: (
    newOptions: Partial<GeneralInfoDropdownOptions>
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<AutoImportFormData>>;
}

const AutoImportFormContext = createContext<
  AutoImportFormContextProps | undefined
>(undefined);

interface AutoImportFormProviderProps {
  children: ReactNode;
}

export const AutoImportFormProvider = ({
  children,
}: AutoImportFormProviderProps) => {
  const [formData, setFormData] = useState<AutoImportFormData>(defaultFormData);
  const [error, setError] = useState<string | null>(null);
  const [generalInfoDropdownOptions, setGeneralInfoDropdownOptions] =
    useState<GeneralInfoDropdownOptions>({
      activeClientList: [],
      clientProducts: [],
      clientProductUsers: [],
    });

  const updateFormData = useCallback((newData: Partial<AutoImportFormData>) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  }, []);

  const updateField = useCallback(
    <K extends keyof AutoImportFormData>(
      field: K,
      value: AutoImportFormData[K]
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetFormData = useCallback(() => {
    setFormData(defaultFormData);
    setError(null);
  }, []);

  const updateGeneralInfoDropdownOptions = useCallback(
    (newOptions: Partial<GeneralInfoDropdownOptions>) => {
      setGeneralInfoDropdownOptions((prev) => ({ ...prev, ...newOptions }));
    },
    []
  );

  return (
    <AutoImportFormContext.Provider
      value={{
        formData,
        updateFormData,
        updateField,
        resetFormData,
        error,
        setError,
        generalInfoDropdownOptions,
        updateGeneralInfoDropdownOptions,
        setFormData,
      }}
    >
      {children}
    </AutoImportFormContext.Provider>
  );
};

export const useAutoImportFormContext = (): AutoImportFormContextProps => {
  const context = useContext(AutoImportFormContext);
  if (!context) {
    throw new Error(
      "useAutoImportFormContext must be used within AutoImportFormProvider"
    );
  }
  return context;
};
